// @owners { team: patients-team }
import { ANIMATION, BORDERS, COLORS, type SIZES, SPACING } from '@alto/design-library-tokens';
import { Column, LgSpacing, Pressable, Row, XsPadding } from '@alto/design-system';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { usePrevious } from '~shared/hooks';
import { Icon, StyledTouchable, type StyledTouchableProps } from '~web/features/essentials/primitives';
import { useDeprecatedMediaQuery } from '~web/hooks';

type CarouselContextType = {
  readonly numOfItems: number;
  readonly numOfColumns: number;
};

const CarouselContext = createContext<CarouselContextType | undefined>(undefined);

const useCarouselContext = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error('Carousel.Item must be used within Carousel.Container');
  }
  return context;
};

const StyledColumn = styled(Column)`
  width: 100%;
  max-width: 100%;
`;

const StyledRow = styled(Row)`
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow: hidden;
`;

const PressableArrow = styled(Pressable)`
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: fit-content;
`;

const StyledDiv = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  position: relative;
  width: 0;
`;

const StyledList = styled.ul<{ index: number; numOfItems: number; numOfColumns: number }>`
  list-style: none;
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0;
  width: ${({ numOfItems, numOfColumns }) => `calc(100% * ${numOfItems} / ${numOfColumns})`};
  transform: ${({ index, numOfItems, numOfColumns }) =>
    `translateX(calc(${index} * -100% * ${numOfColumns} / ${numOfItems}))`};
  transition: all ${ANIMATION.SPEEDS.FASTER / 1000}s ease-in-out;
  will-change: transform;
`;

const StyledDotList = styled.ul`
  list-style: none;
  display: flex;
  justify-content: center;
  flex-direction: row;
  padding: 0;
  margin: 0;
  & > li:not(:last-child) {
    margin-right: ${SPACING.STATIC.XS.px};
  }
`;

const StyledDot = styled.li<{ active: boolean; isMobile: boolean } & StyledTouchableProps>`
  border-radius: ${BORDERS.RADIUS.LG.px};
  padding: ${({ isMobile }) => (isMobile ? SPACING.STATIC.XXS.px : SPACING.STATIC.XS.px)};
  ${StyledTouchable}
  background-color:  ${({ active }) => (active ? COLORS.BACKGROUND_COLORS.PRIMARY : COLORS.PALETTE.GREYSCALE.DEFAULT)};
`;

type ContainerProps = {
  readonly numOfItems: number;
  readonly numOfColumns: number;
  readonly children: ReactNode[];
  readonly iconSize?: keyof typeof SIZES.ICON;
  readonly showDots?: boolean;
  readonly onChange?: (index: number, prevIndex?: number) => void;
};

const Container = ({ numOfItems, numOfColumns, children, iconSize = 'MD', showDots, onChange }: ContainerProps) => {
  const [index, setIndex] = useState(0);
  const prevIndex = usePrevious(index);
  const isMobile = !useDeprecatedMediaQuery('SM');
  const dots = useMemo(() => Array(numOfItems).fill(0), [numOfItems]);
  const last = (index + 1) * numOfColumns >= numOfItems;
  const showLeftArrow = index > 0;
  const showRightArrow = !last;

  const handleIncrement = () => {
    setIndex((p) => ((p + 1) * numOfColumns >= numOfItems ? p : p + 1));
  };
  const handleDecrement = () => {
    setIndex((p) => (p * numOfColumns <= 0 ? p : p - 1));
  };
  const handleDotClick = (index: number) => () => {
    setIndex(index);
  };

  useEffect(() => {
    if (onChange) onChange(index, prevIndex);
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CarouselContext.Provider value={{ numOfItems, numOfColumns }}>
      <StyledColumn>
        <StyledRow
          centerVertically
          wrap={false}
        >
          {showLeftArrow && (
            <PressableArrow onPress={handleDecrement}>
              <XsPadding>
                <Icon
                  name="chevronleft"
                  size={iconSize}
                  interactive={showLeftArrow}
                  label="view previous products"
                />
              </XsPadding>
            </PressableArrow>
          )}
          <StyledDiv>
            <StyledList
              index={index}
              numOfItems={numOfItems}
              numOfColumns={numOfColumns}
            >
              {children}
            </StyledList>
          </StyledDiv>
          {showRightArrow && (
            <PressableArrow onPress={handleIncrement}>
              <XsPadding>
                <Icon
                  name="chevronright"
                  size={iconSize}
                  interactive={showRightArrow}
                  label="view next products"
                />
              </XsPadding>
            </PressableArrow>
          )}
        </StyledRow>
        {showDots ? (
          <>
            {!isMobile && <LgSpacing />}
            <StyledDotList>
              {dots.map((_, idx) => (
                <StyledDot
                  key={`dot for ${idx + 1}`}
                  active={idx === index}
                  onClick={handleDotClick(idx)}
                  isMobile={isMobile}
                />
              ))}
            </StyledDotList>
          </>
        ) : null}
      </StyledColumn>
    </CarouselContext.Provider>
  );
};

const StyledListItem = styled.li<{ numOfColumns: number; numOfItems: number }>`
  padding: 0;
  margin: 0;
  flex: ${({ numOfItems }) => `0 0 calc(100% / ${numOfItems})`};
  width: ${({ numOfItems }) => `calc(100% / ${numOfItems})`};
  max-width: ${({ numOfItems }) => `calc(100% / ${numOfItems})`};
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
`;

type ItemProps = {
  readonly numOfColumns: number;
  readonly children: ReactNode;
};

const Item = ({ numOfColumns, children }: ItemProps) => {
  const { numOfItems } = useCarouselContext();
  return (
    <StyledListItem
      numOfColumns={numOfColumns}
      numOfItems={numOfItems}
    >
      {children}
    </StyledListItem>
  );
};

export const Carousel = { Container, Item };
